/**
 * BSP Knowledge Chatbot - Main App Component
 */

import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { ChatInterface } from './components/ChatInterface';
import { Sidebar } from './components/Sidebar';
import { LoginButton } from './components/LoginButton';
import { isAuthConfigured } from './auth/authConfig';
import { useConversations } from './hooks/useConversations';
import { useState } from 'react';

function App() {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const {
    conversations,
    activeConversation,
    activeConversationId,
    createConversation,
    addMessage,
    deleteConversation,
    selectConversation,
    startNewChat,
  } = useConversations();

  const handleNewChat = () => {
    startNewChat();
  };

  const handleSelectConversation = (id: string) => {
    selectConversation(id);
    setSidebarOpen(false); // Close sidebar on mobile after selection
  };

  // If auth is not configured (development), show chat directly
  if (!isAuthConfigured) {
    return (
      <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
        <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        <div className="flex-1 flex min-h-0">
          <Sidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            onNewChat={handleNewChat}
            conversations={conversations}
            activeConversationId={activeConversationId}
            onSelectConversation={handleSelectConversation}
            onDeleteConversation={deleteConversation}
          />
          <main className="flex-1 flex min-h-0 overflow-hidden">
            <ChatInterface
              key={activeConversationId || 'new'}
              conversationId={activeConversationId}
              initialMessages={activeConversation?.messages || []}
              onCreateConversation={createConversation}
              onAddMessage={addMessage}
            />
          </main>
        </div>
      </div>
    );
  }

  // Show loading during auth
  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Authenticating...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-10 h-10 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              BSP Knowledge Assistant
            </h1>
            <p className="text-gray-600">
              Your AI-powered guide to Biostatistics and Statistical Programming
              processes
            </p>
          </div>
          <LoginButton />
          <p className="text-center text-sm text-gray-500 mt-4">
            Sign in with your Veristat account
          </p>
        </div>
      </div>
    );
  }

  // Show main app when authenticated
  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} showUserMenu />
      <div className="flex-1 flex min-h-0">
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onNewChat={handleNewChat}
          conversations={conversations}
          activeConversationId={activeConversationId}
          onSelectConversation={handleSelectConversation}
          onDeleteConversation={deleteConversation}
        />
        <main className="flex-1 flex min-h-0 overflow-hidden">
          <ChatInterface
            key={activeConversationId || 'new'}
            conversationId={activeConversationId}
            initialMessages={activeConversation?.messages || []}
            onCreateConversation={createConversation}
            onAddMessage={addMessage}
          />
        </main>
      </div>
    </div>
  );
}

interface HeaderProps {
  onMenuClick: () => void;
  showUserMenu?: boolean;
}

function Header({ onMenuClick, showUserMenu }: HeaderProps) {
  return (
    <header className="bg-primary-700 text-white px-4 py-3 shadow-md flex-shrink-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onMenuClick}
            className="p-2 hover:bg-primary-600 rounded-lg transition-colors lg:hidden"
            aria-label="Toggle sidebar"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-semibold">BSP Knowledge Assistant</h1>
            <p className="text-primary-200 text-xs hidden sm:block">
              Biostatistics & Statistical Programming
            </p>
          </div>
        </div>
        {showUserMenu && <UserMenu />}
      </div>
    </header>
  );
}

function UserMenu() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  return (
    <div className="flex items-center gap-3">
      <div className="hidden sm:block text-right">
        <p className="text-sm font-medium">{account?.name}</p>
        <p className="text-xs text-primary-200">{account?.username}</p>
      </div>
      <button
        onClick={handleLogout}
        className="text-sm bg-primary-600 hover:bg-primary-500 px-3 py-1.5 rounded-lg transition-colors"
      >
        Sign out
      </button>
    </div>
  );
}

export default App;
